import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close, ZoomIn, ZoomOut } from '@mui/icons-material';
import Image from 'next/image';
import FullscreenIcon2 from '../Icon/FullscreenIcon2';
import { isMobile, isTablet } from 'react-device-detect';
import Lightbox from 'yet-another-react-lightbox';
import ExitFullscreenIcon from '../Icon/ExitFullscreenIcon';
import FullscreenIcon from '../Icon/FullscreenIcon';
import GridIcon from '../Icon/GridIcon';
import PauseIcon from '../Icon/PauseIcon';
import PlayIcon from '../Icon/PlayIcon';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

export default function FloorPlanGallery({ slides }) {
  const containerRef = useRef(null);
  const thumbContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);
  const [index, setIndex] = useState(-1);

  const iconSize = isMobile ? 20 : isTablet ? 20 : 24;
  const iconSizeMui = isMobile ? 29 : isTablet ? 32 : 36;

  useEffect(() => {
    setThumbStart(0);
    setCurrentIndex(0);
  }, [slides]);

  useEffect(() => {
    const calcVisible = () => {
      if (thumbContainerRef.current) {
        const containerWidth = thumbContainerRef.current.offsetWidth;
        const thumbWidth = window.innerWidth >= 900 ? 118 : 68;
        const count = Math.floor(containerWidth / thumbWidth);
        setVisibleCount(Math.max(1, count));
      }
    };

    const observer = new ResizeObserver(calcVisible);
    const el = thumbContainerRef.current;
    if (el) observer.observe(el);
    calcVisible();

    return () => {
      if (el) observer.unobserve(el);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (thumbStart + visibleCount > slides.length) {
      setThumbStart(Math.max(0, slides.length - visibleCount));
    }
  }, [visibleCount, slides.length]);

  const handlePrevThumb = () => {
    setThumbStart((prev) => Math.max(0, prev - 1));
  };

  const handleNextThumb = () => {
    setThumbStart((prev) => Math.min(slides.length - visibleCount, prev + 1));
  };

  const visibleThumbnails = slides.slice(thumbStart, thumbStart + visibleCount);

  const plugins = useMemo(() => (index < 0 ? [] : [Counter, Fullscreen, Slideshow, Thumbnails, Zoom]), [index]);

  const locked = useRef(false);

  const handleWheel = useCallback(
    (e) => {
      if (index < 0) return;
      e.preventDefault();
      if (locked.current) return;
      locked.current = true;

      if (e.deltaY > 0) {
        setIndex((prev) => (prev + 1) % slides.length);
      } else if (e.deltaY < 0) {
        setIndex((prev) => (prev - 1 + slides.length) % slides.length);
      }

      setTimeout(() => {
        locked.current = false;
      }, 400);
    },
    [index, setIndex, slides.length],
  );

  useEffect(() => {
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: '1px solid #F5F5F6',
        background: '#fff',
        p: 2,
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          height: '50vh',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {slides[currentIndex]?.src && (
          <Box sx={{ position: 'absolute', top: 45, left: 0, right: 0, bottom: 45 }}>
            <Image
              src={slides[currentIndex]?.src || ''}
              alt={slides[currentIndex]?.alt || 'Image'}
              sizes={'(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 530px'}
              fill
              style={{ objectFit: 'contain' }}
              loading="lazy"
            />
          </Box>
        )}

        <IconButton
          onClick={() => setIndex(currentIndex)}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 2,
            '&:hover': {
              color: (theme) => theme.palette.primary.main,
              backgroundColor: 'transparent',
            },
          }}
        >
          <FullscreenIcon2 />
        </IconButton>
      </Box>

      {slides.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          {thumbStart > 0 && slides.length > visibleCount && (
            <IconButton onClick={handlePrevThumb}>
              <ArrowBackIos fontSize="small" />
            </IconButton>
          )}

          <Box ref={thumbContainerRef} sx={{ display: 'flex', gap: 1, overflow: 'hidden', flexGrow: 1 }}>
            {visibleThumbnails.map((thumb, i) => {
              const realIndex = thumbStart + i;
              return (
                <Box
                  key={realIndex}
                  onClick={() => setCurrentIndex(realIndex)}
                  sx={{
                    width: { xs: 60, md: 100 },
                    height: { xs: 60, md: 100 },
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    p: 1,
                    border: (theme) =>
                      realIndex === currentIndex ? `2px solid ${theme.palette.primary.main}` : '1px solid #ccc',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ position: 'relative', width: '100%', height: '100%', p: 1 }}>
                    <Image
                      src={thumb?.thumbnail || thumb?.src}
                      alt={`Thumbnail ${realIndex + 1}`}
                      sizes="(max-width: 768px) 60px, 100px"
                      fill
                      style={{ objectFit: 'cover' }}
                      loading="lazy"
                    />
                  </Box>

                  {realIndex !== currentIndex && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: '#FFFFFF99',
                        zIndex: 1,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {thumbStart + visibleCount < slides.length && (
            <IconButton onClick={handleNextThumb}>
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* Full Lightbox */}
      <Lightbox
        index={index}
        open={index >= 0}
        close={() => setIndex(-1)}
        slides={slides.map((s) => ({
          src: s.fullSrc || s.src,
          thumbnail: s.thumbnail || s.src,
        }))}
        plugins={plugins}
        counter={{
          container: {
            style: {
              top: isMobile ? 8 : 6,
              left: isMobile ? 40 : 60,
              fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px',
              alignItems: 'center',
            },
          },
        }}
        thumbnails={{
          position: 'bottom',
          showToggle: true,
          gap: isMobile ? 2 : 5,
          imageFit: 'cover',
          padding: 0,
          borderRadius: 8,
          borderColor: 'black',
          hidden: false,
          vignette: false,
        }}
        toolbar={{ buttons: ['thumbnails', 'zoom', 'slideshow', 'fullscreen', 'close'] }}
        carousel={{ finite: false, preload: 2 }}
        animation={{ fade: 400, swipe: 500 }}
        render={{
          iconThumbnailsVisible: () => <GridIcon width={iconSize} height={iconSize} />,
          iconThumbnailsHidden: () => <GridIcon width={iconSize} height={iconSize} />,
          iconZoomIn: () => <ZoomIn sx={{ width: iconSizeMui, height: iconSizeMui }} />,
          iconZoomOut: () => <ZoomOut sx={{ width: iconSizeMui, height: iconSizeMui }} />,
          iconSlideshowPlay: () => <PlayIcon width={iconSize} height={iconSize} />,
          iconSlideshowPause: () => <PauseIcon width={iconSize} height={iconSize} />,
          iconEnterFullscreen: () => <FullscreenIcon width={iconSize} height={iconSize} />,
          iconExitFullscreen: () => <ExitFullscreenIcon width={iconSize} height={iconSize} />,
          iconClose: () => <Close sx={{ width: iconSizeMui, height: iconSizeMui }} />,
        }}
        styles={{
          toolbar: { padding: '16px 12px', gap: isMobile ? '0px' : isTablet ? '6px' : '10px' },
          icon: { width: isTablet ? 40 : 62, height: isTablet ? 40 : 62 },
        }}
      />
    </Box>
  );
}
